import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { Button, Spinner, Input } from 'components/ui';
import { toast } from 'sonner';
import {
  sendWhatsAppTemplateMessage,
  sendWhatsAppTemplateMessageToMultiple
} from 'services/whatsappTemplateService';
import WhatsAppPreview from 'app/pages/settings/sections/TemplateManagement/modals/WhatsAppPreview';
import { formatDate, formatTime } from 'utils/dateUtils';

/**
 * WhatsAppTemplateMessageModal
 * Modal for sending WhatsApp template messages with variable editing and live preview
 */
const WhatsAppTemplateMessageModal = ({
  isOpen,
  onClose,
  template,
  leadData,
  leads = [],
  isBulkMode = false
}) => {
  const [isSending, setIsSending] = useState(false);
  const [variables, setVariables] = useState({});

  // Helper to parse buttons if they're a JSON string
  const parseButtons = (buttons) => {
    if (!buttons) return [];
    if (Array.isArray(buttons)) return buttons;
    if (typeof buttons === 'string') {
      try {
        return JSON.parse(buttons);
      } catch (e) {
        console.error('Error parsing buttons:', e);
        return [];
      }
    }
    return [];
  };

  // Helper to get default values based on lead data (leave empty by default)
  const getDefaultValue = (varName, lead) => {
    if (!lead) return '';

    // Check if it's a numbered variable
    if (/^\d+$/.test(varName)) {
      const mapping = {
        '1': lead.name || lead.Name || '',
        '2': lead.email || lead.Email || '',
        '3': lead.mobile || lead.Mobile || lead.phone || lead.Phone || '',
        '4': lead.company || lead.Company || '',
        '5': lead.source || lead.Source || ''
      };
      return mapping[varName] || '';
    }

    // Handle managed/named variables
    const managedVarMap = {
      'FullName': lead.name || lead.Name || '',
      'Email': lead.email || lead.Email || '',
      'Mobile': lead.mobile || lead.Mobile || lead.phone || '',
      'Phone': lead.mobile || lead.Mobile || lead.phone || '',
      'Company': lead.company || lead.Company || '',
      'Source': lead.source || lead.Source || '',
      'CurrentDate': formatDate(new Date()),
      'CurrentYear': new Date().getFullYear().toString(),
      'CurrentTime': formatTime(new Date()),
      'Amount': ''
    };

    return managedVarMap[varName] || '';
  };

  // Extract ALL variables (both numbered and named) from text
  const extractAllVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
    return matches.map(match => match.replace(/[{}]/g, ''));
  };

  // Extract variables grouped by section
  useEffect(() => {
    if (template && isOpen) {
      const sectionedVars = {
        header: {},
        body: {},
        footer: {}
      };


      // Extract from header
      if (template.HeaderText) {
        const headerVars = extractAllVariables(template.HeaderText);
        headerVars.forEach((varName) => {
          sectionedVars.header[varName] = getDefaultValue(varName, leadData);
        });
      }

      // Extract from body
      if (template.BodyText) {
        const bodyVars = extractAllVariables(template.BodyText);
        bodyVars.forEach((varName) => {
          sectionedVars.body[varName] = getDefaultValue(varName, leadData);
        });
      }

      // Extract from footer
      if (template.FooterText) {
        const footerVars = extractAllVariables(template.FooterText);
        footerVars.forEach((varName) => {
          sectionedVars.footer[varName] = getDefaultValue(varName, leadData);
        });
      }

      setVariables(sectionedVars);
    }
  }, [template, isOpen, leadData]);

  // Handle variable change
  const handleVariableChange = (section, varName, value) => {
    setVariables(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [varName]: value
      }
    }));
  };

  // Replace variables in text - show {{var}} if empty
  const replaceVariables = (text, section) => {
    if (!text) return text;
    let result = text;

    // Get variables for this section
    const sectionVars = variables[section] || {};

    Object.entries(sectionVars).forEach(([varName, value]) => {
      const escapedVarName = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const replacement = value.trim() ? value : `{{${varName}}}`;
      result = result.replace(new RegExp(`\\{\\{${escapedVarName}\\}\\}`, 'g'), replacement);
    });

    return result;
  };

  // Get preview data
  const getPreviewData = () => {
    const buttons = parseButtons(template.Buttons);
    return {
      headerText: replaceVariables(template.HeaderText, 'header'),
      headerFormat: template.HeaderFormat || 'TEXT',
      bodyText: replaceVariables(template.BodyText, 'body'),
      footerText: replaceVariables(template.FooterText, 'footer'),
      buttons: buttons.map(btn => ({
        ...btn,
        text: btn.text,
        url: btn.url
      })),
      mediaUrl: template.MediaUrl || null
    };
  };

  // Build final message
  const buildFinalMessage = () => {
    let message = '';

    if (template.HeaderText) {
      message += replaceVariables(template.HeaderText, 'header') + '\n\n';
    }

    if (template.BodyText) {
      message += replaceVariables(template.BodyText, 'body');
    }

    if (template.FooterText) {
      message += '\n\n' + replaceVariables(template.FooterText, 'footer');
    }

    return message.trim();
  };

  // Handle send
  const handleSend = async () => {
    if (!template.Name) {
      toast.error('Template name is required');
      return;
    }

    try {
      setIsSending(true);

      if (isBulkMode) {
        const result = await sendWhatsAppTemplateMessageToMultiple(leads, template.Name, variables);
        if (result.success) {
          toast.success(result.message || `Template message sent to ${leads.length} leads`);
          onClose();
        }
      } else {
        const phoneNumber = leadData?.mobile || leadData?.phone;
        if (!phoneNumber) {
          toast.error('Lead does not have a phone number');
          return;
        }

        const result = await sendWhatsAppTemplateMessage(leadData.id, phoneNumber, template.Name, variables);
        if (result.success) {
          toast.success('WhatsApp template message sent successfully');
          onClose();
        }
      }
    } catch (error) {
      console.error('Error sending WhatsApp template message:', error);
      toast.error(error.message || 'Failed to send template message');
    } finally {
      setIsSending(false);
    }
  };

  if (!template) return null;

  const previewData = getPreviewData();
  const hasHeaderVars = Object.keys(variables.header || {}).length > 0;
  const hasBodyVars = Object.keys(variables.body || {}).length > 0;
  const hasFooterVars = Object.keys(variables.footer || {}).length > 0;
  const hasAnyVariables = hasHeaderVars || hasBodyVars || hasFooterVars;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[250]" onClose={() => { }}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 text-center sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-7xl transform overflow-hidden rounded-xl bg-white dark:bg-dark-800 text-left align-middle shadow-2xl transition-all flex flex-col h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 bg-gradient-to-r from-[#075E54] to-[#128C7E] text-white px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <DialogTitle as="h3" className="text-lg font-semibold text-white">
                          Send WhatsApp Template
                        </DialogTitle>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-sm text-white/90 font-medium">
                            {template.Name || 'Template'}
                          </span>
                          <span className="text-white/60">•</span>
                          <span className="text-sm text-white/80">
                            {isBulkMode ? `${leads.length} recipients` : leadData?.name || 'Contact'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button onClick={onClose} variant="flat" isIcon className="w-10 h-10 rounded-full text-white hover:bg-white/10 transition-colors">
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden min-h-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                    {/* Left - Variables */}
                    <div className="h-full overflow-y-auto px-6 py-5 space-y-5 bg-gray-50/50 dark:bg-dark-900/30 border-r border-gray-200 dark:border-dark-700">
                      <div className="pb-3 border-b border-gray-200 dark:border-dark-700">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <PencilSquareIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          Customize Variables
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 ml-10">
                          Fill in the variables to personalize your message
                        </p>
                      </div>

                      <div className="space-y-4">
                        {/* Header Variables */}
                        {hasHeaderVars && (
                          <div className="space-y-2">
                            <h4 className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide px-1">
                              Header Variables
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              {Object.entries(variables.header).map(([varName, value]) => {
                                const isNumbered = /^\d+$/.test(varName);
                                return (
                                  <div key={`header-${varName}`} className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                                      <span className="font-mono">{`{{${varName}}}`}</span>
                                      {!isNumbered && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-[10px] font-medium">
                                          Auto
                                        </span>
                                      )}
                                    </label>
                                    <Input
                                      value={value}
                                      onChange={(e) => handleVariableChange('header', varName, e.target.value)}
                                      placeholder="Value"
                                      className="w-full text-sm"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Body Variables */}
                        {hasBodyVars && (
                          <div className="space-y-2">
                            <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide px-1">
                              Body Variables
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              {Object.entries(variables.body).map(([varName, value]) => {
                                const isNumbered = /^\d+$/.test(varName);
                                return (
                                  <div key={`body-${varName}`} className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                                      <span className="font-mono">{`{{${varName}}}`}</span>
                                      {!isNumbered && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-[10px] font-medium">
                                          Auto
                                        </span>
                                      )}
                                    </label>
                                    <Input
                                      value={value}
                                      onChange={(e) => handleVariableChange('body', varName, e.target.value)}
                                      placeholder="Value"
                                      className="w-full text-sm"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Footer Variables */}
                        {hasFooterVars && (
                          <div className="space-y-2">
                            <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide px-1">
                              Footer Variables
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                              {Object.entries(variables.footer).map(([varName, value]) => {
                                const isNumbered = /^\d+$/.test(varName);
                                return (
                                  <div key={`footer-${varName}`} className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                                      <span className="font-mono">{`{{${varName}}}`}</span>
                                      {!isNumbered && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-[10px] font-medium">
                                          Auto
                                        </span>
                                      )}
                                    </label>
                                    <Input
                                      value={value}
                                      onChange={(e) => handleVariableChange('footer', varName, e.target.value)}
                                      placeholder="Value"
                                      className="w-full text-sm"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Raw Template Preview */}
                      <div className="pt-4 border-t border-gray-200 dark:border-dark-700">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                            <ChatBubbleLeftRightIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                          </div>
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Template Structure
                          </h4>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-600 leading-relaxed">
                          {template.HeaderText && (
                            <div className="mb-2.5 font-semibold text-purple-600 dark:text-purple-400">
                              {template.HeaderText}
                            </div>
                          )}
                          {template.BodyText && (
                            <div className="mb-2.5 text-gray-700 dark:text-gray-300">{template.BodyText}</div>
                          )}
                          {template.FooterText && (
                            <div className="text-gray-500 dark:text-gray-500 italic text-[11px]">
                              {template.FooterText}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right - Preview */}
                    <div className="h-full overflow-y-auto px-6 py-5 space-y-5 bg-white dark:bg-dark-800">
                      <div className="pb-3 border-b border-gray-200 dark:border-dark-700">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <ChatBubbleLeftRightIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          Live Preview
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 ml-10">
                          See how your message will appear
                        </p>
                      </div>

                      <div className="flex justify-center items-start pt-4">
                        <WhatsAppPreview
                          headerText={previewData.headerText}
                          headerFormat={previewData.headerFormat}
                          bodyText={previewData.bodyText}
                          footerText={previewData.footerText}
                          buttons={previewData.buttons}
                          mediaUrl={previewData.mediaUrl}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 bg-gray-50 dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      {isBulkMode ? (
                        <span><strong className="text-gray-900 dark:text-white">{leads.length}</strong> recipient(s)</span>
                      ) : (
                        <span><strong className="text-gray-900 dark:text-white">{leadData?.name || 'Contact'}</strong></span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Button onClick={onClose} variant="outlined" disabled={isSending} className="min-w-[100px] border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                        Cancel
                      </Button>
                      <button
                        onClick={handleSend}
                        disabled={isSending}
                        className="flex items-center gap-2 px-5 py-2.5 min-w-[140px] justify-center bg-[#128C7E] hover:bg-[#075E54] text-white rounded-lg transition-colors shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSending ? (
                          <>
                            <Spinner size="sm" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="w-4 h-4" />
                            <span>Send Message</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WhatsAppTemplateMessageModal;
